/* eslint-disable @typescript-eslint/no-magic-numbers */
import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  computed,
  input,
  viewChild,
} from '@angular/core';
import * as d3 from 'd3';

import { D3ChartSvg } from '@shared/types/common-graph.types';

import { DepotOverviewChartViewModel } from './depot-overview-chart.view-model';

@Component({
  selector: 'app-depot-overview-chart',
  imports: [NgClass],
  templateUrl: './depot-overview-chart.component.html',
  styleUrl: './depot-overview-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepotOverviewChartComponent<T extends string> implements OnChanges, AfterViewInit {
  public vm = input.required<DepotOverviewChartViewModel<T>>();
  public label = input.required<string>();
  public size = input<'120' | '250'>('250');
  public total = computed((): number => {
    const data = this.vm().data;
    const dataValues = Object.values(data) as number[];

    return dataValues.reduce((acc, value) => acc + (value ?? 0), 0);
  });

  public container = viewChild<ElementRef<HTMLDivElement>>('container');

  private svg?: D3ChartSvg;
  private fullWidth!: number;
  private readonly padding = 40;
  private chartRadiusPx!: number;
  private dashStrokeWidth!: number;
  private dashStrokeDashArray!: string;
  private cornerRadius!: number;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['vm'] && this.svg) {
      this.svg.remove();
      this.generateChart();
    }
  }

  public ngAfterViewInit(): void {
    this.generateChart();
  }

  private generateChart(): void {
    this.setDimensions();
    this.drawChart();
  }

  private setDimensions(): void {
    const container = this.container()!.nativeElement;
    const { width: fullWidth } = container.getBoundingClientRect();
    this.fullWidth = fullWidth;

    this.chartRadiusPx = this.size() === '120' ? 19 : 40;
    this.dashStrokeWidth = this.size() === '120' ? 11 : 23;
    this.dashStrokeDashArray = this.size() === '120' ? '1 3' : '1 5';
    this.cornerRadius = this.size() === '120' ? 2 : 4;
  }

  private drawChart(): void {
    const radius = this.fullWidth / 2;
    const svgWidth = this.fullWidth + this.padding * 2;

    this.svg = d3
      .select(this.container()!.nativeElement)
      .append('svg')
      .attr('width', svgWidth)
      .attr('height', svgWidth)
      .attr('class', 'absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[-1] pointer-events-none');

    const chartG = this.svg.append('g').attr('transform', `translate(${svgWidth / 2},${svgWidth / 2})`);

    const pie = d3
      .pie<[T, number]>()
      .sort(null) // Do not sort group by size
      .value((d) => {
        return d[1];
      });
    const dataReady = pie((Object.entries(this.vm().data) as [T, number][]).filter((entry) => entry[1]));

    // The arc generator
    const arc = d3
      .arc()
      .innerRadius(radius - this.chartRadiusPx) // This is the size of the donut hole
      .outerRadius(radius)
      .padAngle(0.02)
      .cornerRadius(this.cornerRadius);

    const dashArc = d3
      .arc()
      .innerRadius(radius - this.chartRadiusPx / 2) // This is the size of the donut hole
      .outerRadius(radius - this.chartRadiusPx / 2)
      .padAngle(0.08);

    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    chartG
      .selectAll('allSlices')
      .data(dataReady)
      .enter()
      .append('path')
      .attr('d', arc as never)
      .attr('class', (d) => {
        const enumValue = d.data[0];
        const { fillColorClass, dropShadowClass } = this.vm().propertiesMap[enumValue];

        return `${fillColorClass} ${dropShadowClass}`;
      });

    // Build the dash chart: Basically, each part of the pie is a path that we build using the arc function.
    chartG
      .selectAll('allDashes')
      .data(dataReady)
      .enter()
      .append('path')
      .attr('d', (datum) => {
        const pathD = dashArc(datum as never)!;

        const draws = pathD.split('M');
        if (draws.length > 2) {
          return `${draws[0]}M${draws[1]}`;
        }

        const pathSegments = pathD.split('A');

        if (pathSegments.length < 2) {
          return pathD;
        }

        return `${pathSegments[0]}A${pathSegments[1]}`;
      })
      .attr('fill', 'none')
      .attr('stroke-width', this.dashStrokeWidth)
      .attr('stroke-dasharray', this.dashStrokeDashArray)
      .attr('class', 'stroke-foreground-primary-100 dark:stroke-foreground-secondary-100');
  }
}
